import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    // Also listen for variant-selected events as a fallback
    closestSection.addEventListener('variant-selected', this.updatePriceFromVariant);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('variant-selected', this.updatePriceFromVariant);
  }

  /**
   * Updates the price from variant data stored in JSON
   */
  updatePriceFromVariant = (event) => {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return;

    const selectedVariant = variantPicker.selectedOption;
    if (!selectedVariant) return;

    const variantId = selectedVariant.dataset.variantId;
    if (!variantId) return;

    // Get variant data from JSON script tag
    const jsonScript = document.querySelector('[data-product-price-json]');
    if (!jsonScript) return;

    try {
      const productData = JSON.parse(jsonScript.textContent);
      const variant = productData.variants.find(v => v.id.toString() === variantId);
      
      if (variant) {
        this.updatePriceDisplay(variant);
      }
    } catch (error) {
      console.error('Error parsing product price data:', error);
    }
  };

  /**
   * Updates the price display with variant data
   */
  updatePriceDisplay(variant) {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const priceElement = priceContainer.querySelector('.price');
    if (!priceElement) return;

    // Format price (this is a simple version, adjust based on your currency settings)
    const formattedPrice = this.formatMoney(variant.price);
    
    // Update main price
    priceElement.textContent = formattedPrice;

    // Handle compare at price (sale price)
    const compareAtPriceElement = priceContainer.querySelector('.compare-at-price');
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const formattedComparePrice = this.formatMoney(variant.compare_at_price);
      
      if (compareAtPriceElement) {
        compareAtPriceElement.textContent = formattedComparePrice;
        compareAtPriceElement.closest('[role="group"]')?.style.setProperty('display', '');
      }
    } else {
      // Hide compare price if not applicable
      if (compareAtPriceElement) {
        compareAtPriceElement.closest('[role="group"]')?.style.setProperty('display', 'none');
      }
    }
  }

  /**
   * Format money - basic version
   */
  formatMoney(currentPrice) {
    const amount = (currentPrice / 100).toFixed(2);
    // This is a simple formatter - Shopify themes usually have a more complex one
    // Adjust the currency symbol based on your store settings
    return `₹₹{amount}`;
  }

  /**
   * Updates the price from the variant update event (original method)
   */
  updatePrice = (event) => {
    if (!event.detail || !event.detail.data) {
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (!event.detail.data.html) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) {
      return;
    }

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}